/**
 * Typescript has no native support for switch + enum exhaustiveness checking.
 *
 * The workaround is to manually assert that a value is of the `never` type. The
 * typecheck will fail unless a variable has had all its possible types eliminated,
 * leaving only `never`. This can only happen if every possible case was handled
 * and returned in the code.
 *
 * References:
 * - https://www.typescriptlang.org/docs/handbook/basic-types.html#never
 *
 * Usage:
 * ```
 * type AorB = "A" | "B";
 * function test(val: AorB): boolean {
 *   if (val === "A") {
 *     return true;
 *   }
 *
 *   // FAIL, because here, `reveal_type(val) = "B"`
 *   assertExhausted(val);
 *
 *   if (val === "B") {
 *     return false;
 *   }
 *
 *   // PASS, because here, `reveal_type(val) = never`
 *   assertExhausted(val);
 * }
 * ```
 */
export default function assertExhausted(value: never | never[]): never {
  throw new TypeError(
    `RuntimeTypeViolation: Expected 'never', but got '${typeof value}'`,
  );
}
